import Stepper from 'components/Stepper';
import FocusModeTimer from 'components/FocusModeTimer';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useMobile} from 'utils/hooks';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from 'letrus-ui';
import styles from './CompositionHeader.module.scss';
import SubmitCompositionButton from '../SubmitCompositionButton';

export interface CompositionHeaderProps {
  childId?: string;
  parentId?: number;
  testName?: string;
  usesAiComp: boolean;
  onSubmitCallback?(): void;
  isCompositionFinished?: boolean;
  isReviewGridMultigenre?: boolean;
  learningPathUserInstanceId?: string;
  isOffline?: boolean;
  isActive?: boolean;
  remainingSeconds?: number;
  effectiveTimeSeconds?: number;
  isExpired?: boolean;
  editorIsEmpty?: boolean;
}

function CompositionHeader({
  childId,
  parentId,
  testName,
  usesAiComp,
  onSubmitCallback,
  isReviewGridMultigenre,
  learningPathUserInstanceId,
  isCompositionFinished = false,
  isOffline = false,
  isActive = false,
  remainingSeconds = 0,
  effectiveTimeSeconds = 0,
  isExpired = false,
  editorIsEmpty = false,
}: CompositionHeaderProps): JSX.Element {
  const [showDetails, setShowDetails] = useState(true);

  const {isMobile} = useMobile();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const {composition_id} = useParams();
  const navigate = useNavigate();
  const {search} = useLocation();

  const {learningPath, navigateToLearningPath, isWriteStep} =
    useLearningPathNavigation(learningPathUserInstanceId);
  const isLearningPathExpired = learningPath?.status === 'expired' && !childId;

  useEffect(() => {
    if (isMobile) setShowDetails(false);
  }, [isMobile]);

  function onCompositionSubmit(status: string) {
    if (status === 'finished') {
      // A finished write step must land on the composition feedback, wherever it sits in the
      // trail — advancing the trail from here would skip it, and the feedback is the only place a
      // zeroed composition (foreign language / multigenre, the sole zeroing path in the simplified
      // flow) shows its reasons. NewReviewed then self-routes to the zeramento view when zeroed.
      if (isWriteStep(Number(composition_id))) {
        const route = usesAiComp
          ? `/atividades/escrita/${composition_id}/devolutiva/central`
          : `/atividades/escrita/${composition_id}/nova_devolutiva/apresentacao`;
        navigate({pathname: route, search});
        return;
      }

      navigateToLearningPath.nextStep(Number(composition_id));
      return;
    }

    if (isActive && editorIsEmpty) return;

    onSubmitCallback?.();
  }

  function toggleDetailsVisibility() {
    setShowDetails((prev) => !prev);
    titleRef.current?.focus();
  }

  return (
    <>
      <div className={styles.header} aria-live="assertive">
        {/* should be expansible for mobile */}
        <div className={styles.title}>
          <Button
            variant="tertiary"
            aria-label="Voltar"
            data-testid="return-button"
            onClick={() =>
              navigateToLearningPath.previousStep(Number(composition_id))
            }
          >
            <FontAwesomeIcon icon="chevron-left" />
          </Button>

          <h1
            className={showDetails ? styles.expanded : ''}
            tabIndex={-1}
            ref={titleRef}
          >
            {testName}
          </h1>
        </div>

        {showDetails && (
          <Stepper
            learningPath={learningPath}
            onStepClick={navigateToLearningPath.stepByOrder}
          />
        )}

        <div className={styles.actions}>
          {isActive && (
            <FocusModeTimer
              remainingSeconds={remainingSeconds}
              effectiveTimeSeconds={effectiveTimeSeconds}
              isExpired={isExpired}
              isInHeader
            />
          )}
          <SubmitCompositionButton
            usesAiComp={usesAiComp}
            parentId={parentId}
            className={isMobile ? styles.mobileSubmit : ''}
            onClick={(status) => onCompositionSubmit(status)}
            isMultigenreRewriting={!!isReviewGridMultigenre && !!parentId}
            isFinished={isLearningPathExpired || isCompositionFinished}
            disabled={
              isOffline ||
              (isActive &&
                editorIsEmpty &&
                !(isLearningPathExpired || isCompositionFinished))
            }
          />
        </div>
      </div>

      {isMobile && (
        <Button
          variant="tertiary"
          aria-controls="stepper"
          aria-expanded={showDetails}
          className={styles.seeMoreButton}
          onClick={() => toggleDetailsVisibility()}
        >
          <FontAwesomeIcon
            icon={showDetails ? 'chevron-up' : 'chevron-down'}
            size="sm"
          />
          {showDetails ? 'Ver Menos' : 'Ver Mais'}
        </Button>
      )}
    </>
  );
}

export default CompositionHeader;
